import React, { useContext, useEffect, useState } from 'react';
import {
  Box, CircularProgress, Divider, Typography,
} from '@mui/material';
import useSettings from '../hooks/useSettings.ts';
import CartContext, { useCart } from '../contexts/CartContext';
import { CartItemType } from '../types';
import { KEY_ORDER_TYPE_DEPOSIT, KEY_ORDER_TYPE_FULL } from '../constants';
import dayjs from 'dayjs';
import { calculateDiscountedPrice } from '../utils/index.tsx';

const ThankYouPage: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const {
    loading,
    showDescription,
    description,
    smallImage,
    mediumImage,
    largeImage,
  } = useSettings();

  const { depositTotal, payInFullTotal, checkoutProcessingFee, checkoutFeeName, depositTotalWithDiscounts, tuitionTotal, grandTotal, orderType } = useCart();
  const { cartItems } = useContext(CartContext) as unknown as { cartItems: CartItemType };

  const imageSelector = async () => {
    if (largeImage && window.innerWidth > 1080) {
      setSelectedImage(largeImage);
      return;
    }

    if (mediumImage && window.innerWidth > 450) {
      setSelectedImage(mediumImage);
      return;
    }

    setSelectedImage(smallImage);
  };

  useEffect(() => {
    imageSelector();
  }, [largeImage, mediumImage, smallImage]);

  return (
    <div className="event-page p-5 mb-20">
      <Box
        sx={{
          position: 'absolute',
          top: 100,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 5,
        }}
      >
        {loading ? (<CircularProgress />)
          : (
            <>
              {selectedImage && (<img style={{ maxHeight: 200 }} src={selectedImage} alt="Banner" />)}
              <Typography
                fontSize={30}
                fontWeight={700}
                textAlign="center"
                color="#4E4E4E"
              >
                {showDescription && (description || 'Thank you for your purchase')}
              </Typography>
              <Box width="100%" maxWidth={600} paddingX={2}>
                <Box style={{ border: '1px solid #c2c2c2', padding: '20px 20px 20px 20px', borderRadius: '4px' }} width='100%'>
                  <Typography variant="h6" style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#4E4E4E', fontSize: '22px' }}>
                      Total Charge:
                    </span>
                    <span style={{ textAlign: 'right', marginBottom: '15px' }}>
                      <b>${grandTotal.toFixed(2)}</b>
                    </span>
                  </Typography>
                  <Divider />
                  {orderType === KEY_ORDER_TYPE_DEPOSIT && <Typography variant="h6" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '15px'
                  }}>
                    <span style={{
                      color: '#4E4E4E',
                      fontSize: '22px'
                    }}>
                      Total Deposit Paid:
                    </span>
                    <span style={{
                      textAlign: 'right'
                    }}>
                      <b>${depositTotalWithDiscounts.toFixed(2)}</b>
                    </span>
                  </Typography>
                  }
                  {checkoutProcessingFee > 0 && <Typography variant="h6" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '15px'
                  }}>
                    <span style={{
                      color: '#4E4E4E',
                      fontSize: '22px'
                    }}>
                      Processing Fee{ checkoutFeeName ? ` (${checkoutFeeName})` : '' }:
                    </span>
                    <span style={{
                      textAlign: 'right'
                    }}>
                      <b>${Number(checkoutProcessingFee).toFixed(2)}</b>
                    </span>
                  </Typography>
                  }
                  {orderType === KEY_ORDER_TYPE_FULL && payInFullTotal.toFixed(2) !== grandTotal.toFixed(2) && <Typography variant="h6" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '15px'
                  }}>
                    <span style={{
                      color: '#4E4E4E',
                      fontSize: '22px'
                    }}>
                      Paid In Full Discount:
                    </span>
                    <span style={{
                      textAlign: 'right'
                    }}>
                      <b>${(Number(grandTotal) - Number(payInFullTotal)).toFixed(2)}</b>
                    </span>
                  </Typography>
                  }
                  {orderType === KEY_ORDER_TYPE_FULL && <Typography variant="h6" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '15px'
                  }}>
                    <span style={{
                      color: '#4E4E4E',
                      fontSize: '22px'
                    }}>
                      Total Paid:
                    </span>
                    <span style={{
                      textAlign: 'right'
                    }}>
                      <b>${Number(grandTotal.toFixed(2)) > 0 ? (orderType === KEY_ORDER_TYPE_FULL ? payInFullTotal : grandTotal).toFixed(2) : '0.00'}</b>
                    </span>
                  </Typography>
                  }
                  <Typography variant="h6" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px' }}>
                    <span style={{ color: '#4E4E4E', fontSize: '22px' }}>
                      Remaining Balance Due:
                    </span>
                    <span style={{ textAlign: 'right' }}>
                      <b>${(orderType === KEY_ORDER_TYPE_FULL ? 0.00 : tuitionTotal - depositTotal).toFixed(2)}</b>
                    </span>
                  </Typography>
                </Box>
                {cartItems.data !== undefined && cartItems.data.length > 0 && cartItems.data.map((cartItem) => (
                  <Box style={{ border: '1px solid #c2c2c2', padding: '20px 20px 20px 20px', borderRadius: '4px', marginTop: '15px', color: '#667', fontSize: '20px' }} width='100%'>
                    <Box style={{ textAlign: 'center' }}>
                      <b>
                        <span className="item-header">
                          {`${cartItem.item.event.name} for ${cartItem.child?.firstName} ${cartItem.child?.lastName}`}
                        </span>
                      </b>
                    </Box>
                    <Box style={{ textAlign: 'center' }}>
                      <span className="item-header">
                        Event Date:{' '}
                        <b><span>{dayjs(cartItem.item.event.startDate).format('MM/DD/YYYY')} - {dayjs(cartItem.item.event.endDate).format('MM/DD/YYYY')}</span></b>
                      </span>
                    </Box>
                    <Box style={{ textAlign: 'center' }}>
                      <span className="item-header">
                        Purchased Schedule:{' '}
                        <b><span>
                          {
                            cartItem.item.isSelectScheduleAllowed ? (
                              (cartItem.sunday ? 'San, ' : '') +
                              (cartItem.monday ? 'Mon, ' : '') +
                              (cartItem.tuesday ? 'Tue, ' : '') +
                              (cartItem.wednesday ? 'Wed, ' : '') +
                              (cartItem.thursday ? 'Thu, ' : '') +
                              (cartItem.friday ? 'Fri, ' : '') +
                              (cartItem.saturday ? 'Sat, ' : '')
                            ).slice(0, -2) : (
                              (cartItem.item.sun ? 'San, ' : '') +
                              (cartItem.item.mon ? 'Mon, ' : '') +
                              (cartItem.item.tue ? 'Tue, ' : '') +
                              (cartItem.item.wed ? 'Wed, ' : '') +
                              (cartItem.item.thu ? 'Thu, ' : '') +
                              (cartItem.item.fri ? 'Fri, ' : '') +
                              (cartItem.item.sat ? 'Sat, ' : '')
                            ).slice(0, -2)
                          }

                        </span></b>
                      </span>
                    </Box>
                    <Box style={{ textAlign: 'center' }}>
                      <span className="item-header">
                        Total Charge:{' '}
                        <b><span>${cartItem.item.chargePerPeriod}</span></b>
                      </span>
                    </Box>
                    {orderType === KEY_ORDER_TYPE_DEPOSIT && <Box style={{ textAlign: 'center' }}>
                      <span className="item-header">
                        Deposit Paid:{' '}
                        <b><span>${cartItem.item.depositAmount}</span></b>
                      </span>
                    </Box>
                    }
                    {orderType === KEY_ORDER_TYPE_FULL && payInFullTotal.toFixed(2) !== grandTotal.toFixed(2) && <Box style={{ textAlign: 'center' }}>
                      <span className="item-header">
                        Total Paid:{' '}
                        <b><span>${calculateDiscountedPrice(cartItem.item.chargePerPeriod, cartItem.item.discountType, cartItem.item.discountAmount, 0)}</span></b>
                      </span>
                    </Box>
                    }
                    {orderType === KEY_ORDER_TYPE_FULL && <Box style={{ textAlign: 'center' }}>
                      <span className="item-header">
                        Paid In Full Discount:{' '}
                        <b><span>${(Number(cartItem.item.chargePerPeriod) - Number(calculateDiscountedPrice(cartItem.item.chargePerPeriod, cartItem.item.discountType, cartItem.item.discountAmount, 0))).toFixed(2)}</span></b>
                      </span>
                    </Box>
                    }
                    <Box style={{ textAlign: 'center' }}>
                      <span className="item-header">
                        Remaining Balance Due:{' '}
                        <b><span>${(orderType === KEY_ORDER_TYPE_FULL ? 0.00 : Number(cartItem.item.chargePerPeriod) - Number(cartItem.item.depositAmount)).toFixed(2)}</span></b>
                      </span>
                    </Box>
                    {cartItem.item?.autoPaymentDate && <Box style={{ textAlign: 'center' }}>
                      <span className="item-header">
                        Payment Auto Charge Date:{' '}
                        <b><span>{dayjs(cartItem.item.autoPaymentDate).format('MM/DD/YYYY')}</span></b>
                      </span>
                    </Box>
                    }
                  </Box>
                ))}
              </Box>
              <Typography
                sx={{ backgroundColor: '#fff', padding: 1, borderRadius: 1, border: '1px solid #ccc' }}
                bottom={10}
                color="#666666"
                fontSize={15}
                textAlign="center"
              >
              <span>
                Powered by{' '}
                <a href="https://www.kindertales.com/" style={{ textDecoration: 'underline' }}>
                  Kindertales
                </a>
              </span>
              </Typography>
            </>
          )}
      </Box>
    </div >
  );
};

export default ThankYouPage;
